import {
  createFeedback,
  editUserSOrgRole,
  manageCommunicationPreference,
  User as OntologyUser,
  Organization,
  saveReportPreference,
  stripeGetCurrentSubscriptionByEmail,
} from "@unruly-osdk/sdk";
import type { ReportLength } from "../components/UserMenu/ReportPreferencesDialog";
import { User as FoundryUser, Users } from "@osdk/foundry.admin";
import {
  QueryClient,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import { Client, Osdk } from "@osdk/client";
import { checkEnv } from "../helper/helper";
import { combineDependentQuery } from "../helper/queryState";
import {
  LinkedInCallbackError,
  LinkedInCallbackResponse,
  LinkedInCallbackSuccess,
  LinkedInShareEventError,
  LinkedInShareEventRequest,
  LinkedInShareEventResponse,
  LinkedInShareEventSuccess,
  Visibility,
} from "@unruly-global/api-types";

const backendApiUrl = import.meta.env.VITE_BACKEND_API_URL;
checkEnv(backendApiUrl, "VITE_BACKEND_API_URL");

const CUSTOM_REPORTS_BETA_FEATURE = "Custom Report Access";

const keys = {
  all: ["user"] as const,
  current: () => [...keys.all, "current"] as const,
  byFoundryUsername: (username: string | undefined) =>
    [...keys.all, "username", username] as const,
  organizations: (userId: string | undefined) =>
    [...keys.all, "organizations", userId] as const,
  stripeDetails: (email: string | undefined) =>
    [...keys.all, "stripe", email] as const,
};

function UserService(queryClient: QueryClient, apiClient: Client) {
  const service = {
    getCurrentFoundryUserAsync: async (): Promise<FoundryUser> => {
      return queryClient.fetchQuery({
        queryKey: keys.current(),
        queryFn: service.getCurrentFoundryUserAsyncInternal,
      });
    },

    getCurrentFoundryUserAsyncInternal: async (): Promise<FoundryUser> => {
      return await Users.getCurrent(apiClient);
    },

    useGetCurrentFoundryUser: (): UseQueryResult<FoundryUser, unknown> => {
      return useQuery({
        queryKey: keys.current(),
        queryFn: service.getCurrentFoundryUserAsyncInternal,
      });
    },

    useGetCurrent: (): UseQueryResult<Osdk.Instance<OntologyUser> | null> => {
      const foundryUserQuery = service.useGetCurrentFoundryUser();
      const userQuery = service.useGetByFoundryUser(foundryUserQuery.data);
      // Without this the Foundry user's failure is invisible: the ontology user
      // query simply stays disabled, which every consumer reads as "loading".
      return combineDependentQuery(foundryUserQuery, userQuery);
    },

    getCurrentAsync: async (): Promise<Osdk.Instance<OntologyUser> | null> => {
      const foundryUser = await service.getCurrentFoundryUserAsync();
      return queryClient.fetchQuery({
        queryKey: keys.byFoundryUsername(foundryUser.username),
        queryFn: async () => service.getByFoundryUserAsyncInternal(foundryUser),
      });
    },

    getByFoundryUserAsyncInternal: async (
      user: FoundryUser,
    ): Promise<Osdk.Instance<OntologyUser> | null> => {
      const res = await apiClient(OntologyUser)
        .where({
          $and: [{ email: { $eq: user.username } }],
        })
        .fetchPage({ $pageSize: 1 });

      return res.data[0] || null;
    },

    useGetByFoundryUser: (
      user: FoundryUser | undefined,
    ): UseQueryResult<Osdk.Instance<OntologyUser> | null> => {
      return useQuery({
        queryKey: keys.byFoundryUsername(user?.username),
        queryFn: async () => service.getByFoundryUserAsyncInternal(user!),
        enabled: !!user,
      });
    },

    useGetEmployer: (
      user: Osdk.Instance<OntologyUser> | undefined,
    ): UseQueryResult<Osdk.Instance<Organization> | null, unknown> => {
      return useQuery({
        queryKey: keys.organizations(user?.id),

        queryFn: async () => {
          // This happens during live reload by Vite. It shouldn't happen in normal circumstances,
          // since we only call it when `event` is defined.
          if (!user?.$link.employer_) {
            return null;
          }
          const res = await user!.$link.employer_.fetchOneWithErrors();
          if (res.error) {
            throw res.error;
          }
          return res.value;
        },

        enabled: !!user,
      });
    },

    useGetStripeDetails: (
      email?: string,
    ): UseQueryResult<
      stripeGetCurrentSubscriptionByEmail.ReturnType | null,
      unknown
    > => {
      return useQuery({
        queryKey: keys.stripeDetails(email),
        queryFn: async () => {
          return await apiClient(
            stripeGetCurrentSubscriptionByEmail,
          ).executeFunction({
            email: email!,
          });
        },
        enabled: !!email,
      });
    },

    createFeedback: async (user: Osdk.Instance<OntologyUser>, text: string) => {
      return await apiClient(createFeedback).applyAction({
        user,
        text,
      });
    },

    updateRole: async (userId: string, role: string, roleDetails: string) => {
      return await apiClient(editUserSOrgRole).applyAction({
        user: userId,
        organization_role: role,
        organization_role_details: roleDetails,
      });
    },

    useUpdateRole: (): UseMutationResult<
      undefined,
      Error,
      { userId: string; role: string; roleDetails: string }
    > => {
      return useMutation({
        mutationFn: async ({ userId, role, roleDetails }) => {
          return await service.updateRole(userId, role, roleDetails);
        },
        onSuccess: async (_data, { role, roleDetails }) => {
          const foundryUser = await service.getCurrentFoundryUserAsync();
          queryClient.setQueryData(
            keys.byFoundryUsername(foundryUser.username),
            (
              oldUser: Osdk.Instance<OntologyUser>,
            ): Osdk.Instance<OntologyUser> => {
              return {
                ...oldUser,
                organizationRole: role,
                organizationRoleDetails: roleDetails,
              };
            },
          );
        },
      });
    },

    linkedInAccessTokenFromCallback: async (
      userId: string,
      url: URL,
      token: string,
    ): Promise<LinkedInCallbackSuccess> => {
      const response = await fetch(backendApiUrl + "/linkedin/callback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId,
          url: url.toString(),
          token,
        }),
      });
      const body: LinkedInCallbackResponse = await response.json();
      if (response.ok) {
        return body as LinkedInCallbackSuccess;
      }
      throw new Error(
        "LinkedIn callback failed: " + (body as LinkedInCallbackError).error,
      );
    },

    useIsInCustomReportsGroup: (): {
      hasAccess: boolean;
      isLoading: boolean;
      isError: boolean;
    } => {
      const { data: user, isLoading, isError } = service.useGetCurrent();
      const hasAccess =
        user?.enabledBetaFeatures?.includes(CUSTOM_REPORTS_BETA_FEATURE) ??
        false;
      // On error `hasAccess` is false because access is unknown, not denied —
      // callers should branch on `isError` before treating it as a refusal.
      return { hasAccess, isLoading, isError };
    },

    useSaveCommunicationPreferences: (): UseMutationResult<
      undefined,
      Error,
      {
        user: Osdk.Instance<OntologyUser>;
        optOut: boolean;
        hourOffset: number;
        timezoneId: string;
      }
    > => {
      return useMutation({
        mutationFn: async ({ user, optOut, hourOffset, timezoneId }) => {
          return await apiClient(manageCommunicationPreference).applyAction({
            opt_out_of_daily_report_emails: optOut,
            report_hour_offset: hourOffset,
            report_timezone: timezoneId,
            user,
          });
        },
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: keys.all });
        },
      });
    },

    useSaveReportPreferences: (): UseMutationResult<
      undefined,
      Error,
      {
        user: Osdk.Instance<OntologyUser>;
        includePotentialRisks: boolean;
        includeActions: boolean;
        reportLength: ReportLength;
        reportDays: number;
        minimumImpactThreshold: number;
        maximumThemesInPdb: number;
      }
    > => {
      return useMutation({
        mutationFn: async ({
          user,
          includePotentialRisks,
          includeActions,
          reportLength,
          reportDays,
          minimumImpactThreshold,
          maximumThemesInPdb,
        }) => {
          return await apiClient(saveReportPreference).applyAction({
            owner: user,
            reportType: "daily report",
            includeRisksAndConsideration: includePotentialRisks,
            includeActions,
            reportSummaryLength: reportLength,
            dayBacktrack: reportDays,
            eventImpactThreshold: minimumImpactThreshold,
            reportMaxThemeCount: maximumThemesInPdb,
            themeCategory: "Topical",
          });
        },
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: keys.all });
        },
      });
    },

    sharePostOnLinkedIn: async (
      userId: string,
      eventId: string,
      userCommentary: string,
      visibility: Visibility,
      token: string,
      returnUrl: string,
    ): Promise<
      Exclude<LinkedInShareEventResponse, LinkedInShareEventError>
    > => {
      const requestBody: LinkedInShareEventRequest = {
        userId,
        eventId,
        userCommentary,
        visibility: visibility,
        callbackUrl:
          location.protocol + "//" + location.host + "/linkedin-callback",
        returnUrl,
        token,
      };
      const response = await fetch(backendApiUrl + "/linkedin/share-event", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      const body: LinkedInShareEventResponse = await response.json();
      if (response.ok) {
        return body as LinkedInShareEventSuccess;
      }
      throw new Error(
        "Sharing event to LinkedIn failed: " +
          (body as LinkedInShareEventError).error,
      );
    },
  };
  return service;
}
export default UserService;
